import {
  inboxWorkflowColumn,
  inboxWorkflowPartialMessage,
  type InboxWorkflowCellComponent,
  type InboxWorkflowCellContext,
  type InboxWorkflowPreconfigurationComponent,
  type InboxWorkflowSelectOption,
  type InboxWorkflowTableColumn,
  type InboxWorkflowTableStepConfig,
} from "@kontextwerk/control-web-frame/inbox-workflow/contract"
import {
  ComparisonCell,
  ContactEditor,
  validateDraft,
  editorReason,
} from "./_modules/customer-verification-editor"
import { contactEditorActions } from "../../../../_shared/_logic/contact-row-actions"
import VerifyCustomerDecisionCell from "./_modules/verify-customer-decision-cell"
import CustomerContactPropertyCell from "./_modules/customer-contact-property-cell"
import CustomerContactSourceCell from "./_modules/customer-contact-source-cell"
import VerifyCustomerPreconfiguration from "./_modules/verify-customer-preconfiguration"
import type {
  VerifyCustomerCommitPayload,
  VerifyCustomerDecision,
  VerifyCustomerDraft,
  VerifyCustomerPreviewPayload,
  VerifyCustomerRow,
} from "./contract"
import { buildVerifyCustomerRows, defaultVerifyCustomerDraft } from "./_logic/tableRows"
import { parseVerifyCustomerPreview } from "./_logic/preview"

const column = (
  id: string,
  label: string,
  cellKey: string,
  extra: Omit<
    Partial<InboxWorkflowTableColumn<VerifyCustomerRow, VerifyCustomerDraft>>,
    "id" | "label" | "cellKey"
  > = {},
) => inboxWorkflowColumn<VerifyCustomerRow, VerifyCustomerDraft>(id, label, cellKey, extra)

const existingTargetPrefix = "existing:"
const existingTargetValue = (customerId: string) => `${existingTargetPrefix}${customerId}`
const existingCustomerIdFromValue = (value: string) =>
  value.startsWith(existingTargetPrefix) ? value.slice(existingTargetPrefix.length) : ""

const stringValue = (value: unknown): string =>
  typeof value === "string" || typeof value === "number" ? String(value).trim() : ""

const verifyDecisionValue = (
  context: InboxWorkflowCellContext<VerifyCustomerRow, VerifyCustomerDraft>,
) => {
  const decision = context.draft.decision

  return decision === "use_existing" && context.draft.customerId
    ? existingTargetValue(context.draft.customerId)
    : decision
}

const verifyDecisionPatch = (
  value: unknown,
  context: InboxWorkflowCellContext<VerifyCustomerRow, VerifyCustomerDraft>,
): Partial<VerifyCustomerDraft> => {
  const next = stringValue(value)

  if (next === "skip") return { decision: "skip", customerId: null }

  if (next === "create_new") return { decision: "create_new", customerId: null }

  const customerId = existingCustomerIdFromValue(next)
  const option = context.row.customerOptions.find(
    (candidate) => candidate.customerId === customerId,
  )

  return option
    ? {
        decision: "use_existing",
        customerId,
        role: option.role,
        email: option.email,
        phone: option.phone,
      }
    : { decision: "create_new", customerId: null }
}

const verifyDecisionOptions =
  (allowSkip: boolean) =>
  (
    context: InboxWorkflowCellContext<VerifyCustomerRow, VerifyCustomerDraft>,
  ): InboxWorkflowSelectOption[] => {
    const options: InboxWorkflowSelectOption[] = [
      {
        value: "create_new",
        label: "Neu anlegen",
        tooltipContent: "Gemeldeten Ansprechpartner neu anlegen und Kontaktmittel übernehmen.",
      },
      ...context.row.customerOptions.map((option) => ({
        value: existingTargetValue(option.customerId),
        label: `${option.displayName} zuordnen`,
        tooltipContent:
          [option.displayName, option.role, option.email, option.phone]
            .filter(Boolean)
            .join(" · ") ||
          "Gemeldeten Ansprechpartner diesem bestehenden Ansprechpartner zuordnen.",
      })),
    ]

    if (allowSkip) {
      options.push({
        value: "skip",
        label: "Ignorieren",
        tooltipContent: "Diese Rückmeldung nicht als Ansprechpartner übernehmen.",
      })
    }

    return options
  }

const verifyCustomerColumns = (allowSkip: boolean) => [
  column("decision", "Als was speichern?", "verifyCustomerDecision", {
    placeholder: "Übernahme",
    value: verifyDecisionValue,
    patch: verifyDecisionPatch,
    options: verifyDecisionOptions(allowSkip),
  }),
  column("addressLabel", "Liegenschaft", "verifyCustomerProperty"),
  column("existingCustomers", "Bisherige Ansprechpartner", "verifyCustomerComparison"),
  column("newCustomer", "Neuer Ansprechpartner", "verifyCustomerComparison"),
  column("sourceLabel", "Herkunft", "verifyCustomerSource"),
]

const verifyPayload = (draft: VerifyCustomerDraft): VerifyCustomerCommitPayload => {
  if (!draft.decision) throw new Error("Ansprechpartner-Entscheidung fehlt.")

  const contact_email = draft.email.trim() || null
  const contact_phone = draft.phone.trim() || null
  const property_customer_role = draft.role || null

  if (draft.decision === "skip") return { decision: "skip" }

  if (draft.decision === "use_existing") {
    return {
      decision: "use_existing",
      customer_id: draft.customerId,
      contact_email,
      contact_phone,
      property_customer_role,
    }
  }

  return {
    decision: "create_new",
    create_customer: { display_name: draft.displayName.trim() },
    contact_email,
    contact_phone,
    property_customer_role,
  }
}

export const verifyCustomerCells: Record<string, InboxWorkflowCellComponent> = {
  verifyCustomerDecision: VerifyCustomerDecisionCell,
  verifyCustomerComparison: ComparisonCell,
  verifyCustomerProperty: CustomerContactPropertyCell,
  verifyCustomerSource: CustomerContactSourceCell,
}

export const createVerifyCustomerStep = (
  allowSkip = true,
  options: { preconfiguration?: InboxWorkflowPreconfigurationComponent | null } = {},
): InboxWorkflowTableStepConfig<
  VerifyCustomerRow,
  VerifyCustomerDraft,
  VerifyCustomerCommitPayload,
  VerifyCustomerPreviewPayload
> => ({
  kind: "table",
  preview: {
    kind: "customer_contact_verification.verify_customer.standard_table",
    parse: parseVerifyCustomerPreview,
    errorMessage: "Ansprechpartner-Optionen konnten nicht geladen werden.",
  },
  columns: verifyCustomerColumns(allowSkip),
  buildRows: ({ workflow, step, rowOutcomes, stepPreview }) =>
    buildVerifyCustomerRows({ workflow, step, rowOutcomes, preview: stepPreview }),
  draft: {
    create: (row) => defaultVerifyCustomerDraft(row, { allowSkip }),
    validate: validateDraft,
  },
  preconfiguration:
    options.preconfiguration === undefined
      ? allowSkip
        ? (VerifyCustomerPreconfiguration as InboxWorkflowPreconfigurationComponent<
            VerifyCustomerRow,
            VerifyCustomerDraft
          >)
        : undefined
      : (options.preconfiguration ?? undefined),
  rowEditor: ContactEditor,
  rowEditorTitle: "Ansprechpartner bearbeiten",
  editorDisabledReason: ({ row, draft }) => editorReason(row, draft),
  rowActions: (props) => contactEditorActions(props, editorReason(props.row, props.draft)),
  buildPayload: (_row, draft) => verifyPayload(draft),
  commit: {
    label: "Ansprechpartner speichern",
    successMessage: "Ansprechpartner wurden gespeichert.",
    partialMessage: inboxWorkflowPartialMessage,
  },
})

export type { VerifyCustomerDecision, VerifyCustomerDraft }
